import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import './Highcharts.less';
import { mappingList } from '../../../helpers/utils';

// Initialize the exporting module
HighchartsExporting(Highcharts);
HighchartsAccessibility(Highcharts);

const WIDTH_OFFSET = 5;

const DonutChart = ({ data = [], config = {}, centerContent = '', onPieClick, selectedIndex, disabledIndex = [], containerProps = {} }) => {
    const { height, colors, locationCnts = [], name = '', selectWidthOffset } = config;
    const chartComponent = useRef(null);
    const [mergedConfig, setMergedConfig] = useState(null);

    useEffect(() => {
        initConfig();
    }, [data, selectedIndex]);

    function initConfig() {
        const formattedData = [...data].map((d) => {
            d.y = d.value;
            return d;
        });

        // Check if all values are zero
        const allValuesZero = formattedData.every((d) => d.value === 0);

        const seriesData = allValuesZero
            ? formattedData.map((d) => ({ ...d, y: 1, color: '#ccc' }))
            : formattedData.map((d, i) => ({ ...d, y: d.value, color: colors[i] }));

        const defaultConfig = {
            title: false,
            subtitle: {
                useHTML: true,
                text: centerContent,
                verticalAlign: 'middle',
                align: 'center'
            },
            legend: {
                align: 'right',
                layout: 'vertical',
                verticalAlign: 'middle',
                width: 130,
                symbolRadius: 0,
                itemStyle: {
                    fontSize: '1em',
                    textOverflow: undefined
                },
                labelFormat: '{name}: <b>{value}</b>'
            },
            chart: {
                height: height,
                events: {
                    render(e) {
                        setTimeout(() => {
                            if (chartComponent.current) {
                                const chart = chartComponent.current.chart;
                                // Reset ALL points' r to their original shapeArgs.r first
                                chart.series[0].data.forEach(function (point) {
                                    if (point.graphic && point.shapeArgs) {
                                        point.graphic.animate({ r: point.shapeArgs.r });
                                        point.customSelect = false;
                                    }
                                });
                                if (!isNaN(selectedIndex) && selectedIndex !== '' && chart.series[0].data[selectedIndex]) {
                                    chart.series[0].data[selectedIndex].select(true, false);
                                    const that = chart.getSelectedPoints()[0];
                                    that.graphic.animate({
                                        r: that.shapeArgs.r + (selectWidthOffset || WIDTH_OFFSET)
                                    });
                                    that.customSelect = true;
                                } else {
                                    // Deselect all points
                                    chart.series[0].data.forEach(function (point) {
                                        point.select(false, false);
                                    });
                                }
                            }
                        }, 100);
                    }
                }
            },
            plotOptions: {
                series: {
                    borderWidth: 0,
                    colorByPoint: true,
                    borderRadius: 0,
                    type: 'pie',
                    size: '100%',
                    innerSize: '80%',
                    legendSymbol: 'rectangle',
                    point: {
                        events: {
                            click(e) {
                                if (config.pieClickEnabled === false) {
                                    return;
                                }
                                let that = this;
                                if (disabledIndex && disabledIndex.includes(that.index)) {
                                    return;
                                }
                                // Reset any previously selected slice
                                that.series.data.forEach(function (point) {
                                    if (point !== that && point.customSelect) {
                                        point.graphic.animate({
                                            r: point.shapeArgs.r
                                        });
                                        point.customSelect = false;
                                    }
                                });
                                that.graphic.animate({
                                    r: that.shapeArgs.r + (that.customSelect ? 0 : (selectWidthOffset || WIDTH_OFFSET))
                                });
                                that.customSelect = !that.customSelect;
                                if (onPieClick) {
                                    setTimeout(function () {
                                        onPieClick(that.name, that.index);
                                    });
                                }
                            },
                            legendItemClick: () => false
                        }
                    }
                },
                pie: {
                    allowPointSelect: true,
                    slicedOffset: 0,
                    states: {
                        select: {
                            width: 50,
                            size: '90%',
                            innerSize: '70%'
                        }
                    },
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: false
                    },
                    showInLegend: true
                }
            },
            tooltip: {
                headerFormat: '',
                fontSize: 12,
                stickyTracking: true,
                useHTML: true,
                pointFormat: '{point.name}: <b>{point.value}</b>',
                borderWidth: 0,
                backgroundColor: 'none',
                ...(locationCnts.length > 0
                    ? {
                          outside: true,
                          formatter: function () {
                              const locationList = locationCnts.map((locationObj) => {
                                  const { location: locName = '' } = locationObj;
                                  const countName = mappingList[name]?.[this.key];
                                  const count = locationObj[this.key] ?? locationObj[countName] ?? 0;
                                  return `${locName}: ${count}`;
                              });

                              return (
                                  '<div style="padding: 10px;background-color: rgba(255,255,255,1);border: 1px solid #a8a7a5;"><div>' +
                                  this.key +
                                  ' ' +
                                  name +
                                  ' </div>' +
                                  locationList.join('<br>') +
                                  '<br><b>All locations: ' +
                                  (allValuesZero ? 0 : this.y) +
                                  '</b></div>'
                              );
                          }
                      }
                    : {})
            },
            series: [
                {
                    type: 'pie',
                    data: seriesData
                }
            ],
            exporting: {
                enabled: false
            }
        };

        setMergedConfig({ ...defaultConfig, ...config });
    }

    return mergedConfig && <HighchartsReact ref={chartComponent} className="chart-container" highcharts={Highcharts} options={mergedConfig} containerProps={containerProps} />;
};

export default DonutChart;
